import { useState, useEffect, useCallback } from 'react'
import {
  getUserDetail,
  verifyUser,
  updateUserRole,
  manageExtraRole,
  toggleProStatus,
  extendPro,
  setProExpiry,
  suspendUser,
  unsuspendUser,
  deleteUserAndData,
  UserDetailResult,
} from '../../utils/adminApi'
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  User,
  Mail,
  Shield,
  CheckCircle2,
  XCircle,
  Crown,
  Calendar,
  CreditCard,
  Users,
  Trash2,
  Clock,
  Activity,
  DollarSign,
  Link,
  Ban,
  RotateCcw,
  Settings,
  Eye,
  EyeOff,
} from 'lucide-react'

type ControlTab = 'account' | 'subscription' | 'payments' | 'referrals' | 'dashboard'

interface AdminUserControlProps {
  userId: string
  onBack: () => void
}

const ROLE_OPTIONS = ['scout', 'club', 'admin', 'sub_admin', 'player']
const EXTRA_ROLES = ['scout', 'club', 'admin', 'sub_admin', 'player']

const STATUS_CONFIG: Record<string, { label: string; icon: any; color: string; bg: string; border: string }> = {
  active: { label: 'Active', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  on_leave: { label: 'On Leave', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  contract: { label: 'Contract', icon: Activity, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  terminated: { label: 'Terminated', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  pending: { label: 'Pending', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  success: { label: 'Success', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  failed: { label: 'Failed', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
}

const ROLE_BADGES: Record<string, { label: string; color: string }> = {
  admin: { label: 'Admin', color: 'bg-green-500/10 text-green-400 border-green-500/30' },
  sub_admin: { label: 'Sub-Admin', color: 'bg-purple-500/10 text-purple-400 border-purple-500/30' },
  scout: { label: 'Scout', color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  club: { label: 'Club', color: 'bg-purple-500/10 text-purple-400 border-purple-500/30' },
  player: { label: 'Player', color: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
}

function formatKobo(kobo: number): string {
  return `₦${(kobo / 100).toLocaleString('en-NG')}`
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function daysRemaining(dateStr?: string | null): number {
  if (!dateStr) return 0
  const diff = new Date(dateStr).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

export default function AdminUserControl({ userId, onBack }: AdminUserControlProps) {
  const [data, setData] = useState<UserDetailResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ControlTab>('account')
  const [processing, setProcessing] = useState<string | null>(null)
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteMode, setDeleteMode] = useState<'archive' | 'delete'>('archive')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [newExpiry, setNewExpiry] = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    const result = await getUserDetail(userId)
    if (result.ok) {
      setData(result)
    } else {
      setError(result.error || 'Failed to load user detail')
    }
    setLoading(false)
  }, [userId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const flash = (type: 'success' | 'error', text: string) => {
    setActionMsg({ type, text })
    setTimeout(() => setActionMsg(null), 4000)
  }

  const run = async (key: string, fn: () => Promise<{ ok: boolean; error?: string }>, successMsg: string) => {
    setProcessing(key)
    const result = await fn()
    setProcessing(null)
    if (result.ok) {
      flash('success', successMsg)
      await loadData()
    } else {
      flash('error', result.error || 'Action failed')
    }
  }

  const handleVerify = (verified: boolean) =>
    run('verify', () => verifyUser(userId, verified), verified ? 'User verified' : 'User unverified')

  const handleRoleChange = (newRole: string) =>
    run('role', () => updateUserRole(userId, newRole), `Role changed to ${newRole}`)

  const handleGrantExtra = (role: string) =>
    run(`grant_${role}`, () => manageExtraRole(userId, role, 'grant'), `Granted ${role} role`)

  const handleRevokeExtra = (role: string) =>
    run(`revoke_${role}`, () => manageExtraRole(userId, role, 'revoke'), `Revoked ${role} role`)

  const handleTogglePro = (active: boolean) =>
    run('toggle_pro', () => toggleProStatus(userId, active, data?.profile?.role || 'player'), active ? 'Pro activated' : 'Pro deactivated')

  const handleExtendPro = (days: number) =>
    run('extend', () => extendPro(userId, days), `Extended Pro by ${days} days`)

  const handleSetExpiry = () => {
    if (!newExpiry) return
    run('set_expiry', () => setProExpiry(userId, newExpiry), 'Expiry date updated')
  }

  const handleSuspend = () =>
    run('suspend', () => suspendUser(userId), 'Account suspended')

  const handleUnsuspend = () =>
    run('unsuspend', () => unsuspendUser(userId), 'Account unsuspended')

  const handleDelete = async () => {
    setDeleteLoading(true)
    const result = await deleteUserAndData(userId, deleteMode)
    setDeleteLoading(false)
    if (result.ok) {
      flash('success', deleteMode === 'delete' ? 'Account permanently deleted' : 'Account suspended')
      setShowDeleteModal(false)
      setTimeout(() => onBack(), 1500)
    } else {
      flash('error', result.error || 'Delete failed')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="text-[#7cff01] animate-spin" size={32} />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="text-gray-500 hover:text-white font-bold text-sm tracking-widest uppercase transition flex items-center gap-2">
          <ArrowLeft size={16} /> Back to Users
        </button>
        <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-6 flex items-center gap-3">
          <AlertCircle size={20} className="text-red-400" />
          <p className="text-red-300">{error || 'User not found'}</p>
        </div>
      </div>
    )
  }

  const profile = data.profile
  const sub = data.subscription
  const isPro = sub?.status === 'active' && sub?.current_period_end && new Date(sub.current_period_end) > new Date()
  const roleBadge = ROLE_BADGES[profile?.role] || ROLE_BADGES.player
  const extraRoles: string[] = profile?.extra_roles || []
  const primaryExtra = extraRoles.filter((r) => r !== profile?.role)

  const tabs: { id: ControlTab; label: string; icon: any }[] = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'subscription', label: 'Subscription', icon: Crown },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'referrals', label: 'Referrals', icon: Link },
    { id: 'dashboard', label: 'Dashboard', icon: Activity },
  ]

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Back */}
      <button onClick={onBack} className="text-gray-500 hover:text-white font-bold text-sm tracking-widest uppercase transition flex items-center gap-2">
        <ArrowLeft size={16} /> Back to Users
      </button>

      {/* Action messages */}
      {actionMsg && (
        <div className={`rounded-xl border p-3 flex items-center gap-2 ${actionMsg.type === 'success' ? 'border-green-500/20 bg-green-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
          {actionMsg.type === 'success' ? <CheckCircle2 size={16} className="text-green-400" /> : <AlertCircle size={16} className="text-red-400" />}
          <p className={`text-sm ${actionMsg.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>{actionMsg.text}</p>
        </div>
      )}

      {/* Header */}
      <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gray-800 flex items-center justify-center border border-gray-700">
            <User size={28} className="text-gray-500" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-display font-black text-white">{profile?.full_name || 'Unknown'}</h2>
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${roleBadge.color}`}>
                {roleBadge.label}
              </span>
              {profile?.is_verified && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-green-500/10 text-green-400 border border-green-500/30">
                  <CheckCircle2 size={10} /> Verified
                </span>
              )}
              {profile?.suspended && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-red-500/10 text-red-400 border border-red-500/30">
                  <Ban size={10} /> Suspended
                </span>
              )}
              {isPro && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                  <Crown size={10} /> PRO
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              <span className="flex items-center gap-1"><Mail size={12} /> {profile?.email}</span>
              <span className="flex items-center gap-1"><Calendar size={12} /> Joined {formatDate(profile?.created_at)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-950 p-1 rounded-xl border border-gray-800 overflow-x-auto">
        {tabs.map((tab) => {
          const TabIcon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition flex items-center gap-2 shrink-0 ${
                activeTab === tab.id
                  ? 'bg-[#7cff01]/10 text-[#7cff01] shadow-[inset_0_0_0_1px_rgba(124,255,1,0.2)]'
                  : 'text-gray-500 hover:text-white'
              }`}
            >
              <TabIcon size={14} /> {tab.label}
            </button>
          )
        })}
      </div>

      {/* ===== ACCOUNT TAB ===== */}
      {activeTab === 'account' && (
        <div className="space-y-6">
          {/* Profile Info */}
          <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4">Profile Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Full Name</p>
                <p className="text-sm text-white">{profile?.full_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Email</p>
                <p className="text-sm text-white">{profile?.email || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Role</p>
                <p className="text-sm text-white capitalize">{profile?.role || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">User ID</p>
                <p className="text-xs text-gray-400 font-mono">{profile?.id}</p>
              </div>
              {primaryExtra.length > 0 && (
                <div className="md:col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Extra Roles</p>
                  <div className="flex gap-2 flex-wrap">
                    {primaryExtra.map((r) => (
                      <span key={r} className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-gray-800 text-gray-300 border border-gray-700">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4">Account Controls</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Verify/Unverify */}
              <button
                onClick={() => handleVerify(!profile?.is_verified)}
                disabled={processing === 'verify'}
                className="flex items-center gap-3 p-4 rounded-xl bg-gray-900 border border-gray-800 hover:border-gray-600 transition text-left disabled:opacity-50"
              >
                {processing === 'verify' ? <Loader2 size={18} className="animate-spin text-gray-400" /> : profile?.is_verified ? <EyeOff size={18} className="text-yellow-400" /> : <Eye size={18} className="text-green-400" />}
                <div>
                  <p className="text-sm font-bold text-white">{profile?.is_verified ? 'Unverify User' : 'Verify User'}</p>
                  <p className="text-xs text-gray-500">{profile?.is_verified ? 'Remove verification badge' : 'Grant verification badge'}</p>
                </div>
              </button>

              {/* Change Role */}
              <div className="p-4 rounded-xl bg-gray-900 border border-gray-800">
                <div className="flex items-center gap-3 mb-2">
                  <Shield size={18} className="text-blue-400" />
                  <p className="text-sm font-bold text-white">Change Role</p>
                </div>
                <select
                  value={profile?.role || ''}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  disabled={processing === 'role'}
                  className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-xs focus:outline-none focus:border-[#7cff01]/50"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>{r.replace('_', ' ').toUpperCase()}</option>
                  ))}
                </select>
              </div>

              {/* Extra Roles */}
              <div className="p-4 rounded-xl bg-gray-900 border border-gray-800">
                <div className="flex items-center gap-3 mb-2">
                  <Settings size={18} className="text-purple-400" />
                  <p className="text-sm font-bold text-white">Extra Roles</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {EXTRA_ROLES.filter((r) => r !== profile?.role).map((r) => {
                    const has = extraRoles.includes(r)
                    return (
                      <button
                        key={r}
                        onClick={() => has ? handleRevokeExtra(r) : handleGrantExtra(r)}
                        disabled={processing === `grant_${r}` || processing === `revoke_${r}`}
                        className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase transition disabled:opacity-50 ${
                          has ? 'bg-[#7cff01]/10 text-[#7cff01] border border-[#7cff01]/20' : 'bg-gray-800 text-gray-500 border border-gray-700 hover:text-white'
                        }`}
                      >
                        {has ? `✓ ${r}` : `+ ${r}`}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Suspend/Unsuspend */}
              <button
                onClick={profile?.suspended ? handleUnsuspend : handleSuspend}
                disabled={processing === 'suspend' || processing === 'unsuspend'}
                className="flex items-center gap-3 p-4 rounded-xl bg-gray-900 border border-gray-800 hover:border-gray-600 transition text-left disabled:opacity-50"
              >
                {processing === 'suspend' || processing === 'unsuspend' ? (
                  <Loader2 size={18} className="animate-spin text-gray-400" />
                ) : profile?.suspended ? (
                  <RotateCcw size={18} className="text-green-400" />
                ) : (
                  <Ban size={18} className="text-red-400" />
                )}
                <div>
                  <p className="text-sm font-bold text-white">{profile?.suspended ? 'Unsuspend Account' : 'Suspend Account'}</p>
                  <p className="text-xs text-gray-500">{profile?.suspended ? 'Restore access' : 'Block login, hide content'}</p>
                </div>
              </button>

              {/* Delete */}
              <button
                onClick={() => { setShowDeleteModal(true); setDeleteMode('archive') }}
                className="flex items-center gap-3 p-4 rounded-xl bg-gray-900 border border-gray-800 hover:border-red-500/30 transition text-left"
              >
                <Trash2 size={18} className="text-red-400" />
                <div>
                  <p className="text-sm font-bold text-white">Delete Account</p>
                  <p className="text-xs text-gray-500">Suspend or permanently delete</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SUBSCRIPTION TAB ===== */}
      {activeTab === 'subscription' && (
        <div className="space-y-6">
          {/* Status Card */}
          <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4">Subscription Status</h3>
            {isPro ? (
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-yellow-500/10 flex items-center justify-center border border-yellow-500/30">
                  <Crown size={28} className="text-yellow-400" />
                </div>
                <div>
                  <p className="text-xl font-display font-black text-yellow-400">PRO Active</p>
                  <p className="text-sm text-gray-400 capitalize">{sub?.role || 'player'} plan</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Expires {formatDate(sub?.current_period_end)} ({daysRemaining(sub?.current_period_end)} days remaining)
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gray-800 flex items-center justify-center border border-gray-700">
                  <User size={28} className="text-gray-500" />
                </div>
                <div>
                  <p className="text-xl font-display font-black text-gray-400">FREE</p>
                  <p className="text-sm text-gray-500">No active subscription</p>
                  {sub?.current_period_end && (
                    <p className="text-xs text-gray-600 mt-1">Last expired {formatDate(sub.current_period_end)}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Subscription Details */}
          {sub && (
            <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4">Subscription Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <p className="text-sm text-white capitalize">{sub.status}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Period Start</p>
                  <p className="text-sm text-white">{formatDateTime(sub.current_period_start)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Period End</p>
                  <p className="text-sm text-white">{formatDateTime(sub.current_period_end)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4">Subscription Controls</h3>
            <div className="space-y-4">
              {/* Make Pro / Deactivate */}
              <button
                onClick={() => handleTogglePro(!isPro)}
                disabled={processing === 'toggle_pro'}
                className="w-full flex items-center gap-3 p-4 rounded-xl bg-gray-900 border border-gray-800 hover:border-yellow-500/30 transition text-left disabled:opacity-50"
              >
                {processing === 'toggle_pro' ? <Loader2 size={18} className="animate-spin text-gray-400" /> : <Crown size={18} className={isPro ? 'text-red-400' : 'text-yellow-400'} />}
                <div>
                  <p className="text-sm font-bold text-white">{isPro ? 'Deactivate Pro' : 'Make Pro'}</p>
                  <p className="text-xs text-gray-500">{isPro ? 'Set subscription to inactive' : 'Grant 30-day Pro subscription'}</p>
                </div>
              </button>

              {/* Extend Pro */}
              <div className="p-4 rounded-xl bg-gray-900 border border-gray-800">
                <div className="flex items-center gap-3 mb-3">
                  <RotateCcw size={18} className="text-green-400" />
                  <p className="text-sm font-bold text-white">Extend Pro</p>
                </div>
                <div className="flex gap-2">
                  {[7, 14, 30, 90, 365].map((days) => (
                    <button
                      key={days}
                      onClick={() => handleExtendPro(days)}
                      disabled={processing === 'extend'}
                      className="px-3 py-2 rounded-lg bg-gray-800 text-gray-300 hover:text-white text-xs font-bold transition border border-gray-700 disabled:opacity-50"
                    >
                      +{days}d
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-2">Extends from current end date (or now if expired)</p>
              </div>

              {/* Set Expiry */}
              <div className="p-4 rounded-xl bg-gray-900 border border-gray-800">
                <div className="flex items-center gap-3 mb-3">
                  <Calendar size={18} className="text-blue-400" />
                  <p className="text-sm font-bold text-white">Set Expiry Date</p>
                </div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={newExpiry}
                    onChange={(e) => setNewExpiry(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-xs focus:outline-none focus:border-[#7cff01]/50"
                  />
                  <button
                    onClick={handleSetExpiry}
                    disabled={!newExpiry || processing === 'set_expiry'}
                    className="px-4 py-2 rounded-lg bg-[#7cff01]/10 text-[#7cff01] text-xs font-bold transition border border-[#7cff01]/20 disabled:opacity-50"
                  >
                    {processing === 'set_expiry' ? <Loader2 size={14} className="animate-spin" /> : 'Set'}
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-2">Set a specific expiry date for the subscription</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== PAYMENTS TAB ===== */}
      {activeTab === 'payments' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-gray-800 bg-[#0d1117] p-4">
              <p className="text-xs font-black uppercase tracking-widest text-gray-500">Total Payments</p>
              <p className="text-2xl font-display font-black text-white mt-1">{data.stats?.totalPayments || 0}</p>
            </div>
            <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
              <p className="text-xs font-black uppercase tracking-widest text-green-500">Total Spent</p>
              <p className="text-2xl font-display font-black text-green-400 mt-1">{formatKobo(data.stats?.totalSpent || 0)}</p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-[#0d1117] p-4">
              <p className="text-xs font-black uppercase tracking-widest text-gray-500">Transactions</p>
              <p className="text-2xl font-display font-black text-white mt-1">{data.payments?.length || 0}</p>
            </div>
          </div>

          {/* Payments Table */}
          <div className="rounded-2xl border border-gray-800 bg-[#0d1117] overflow-hidden">
            <div className="p-4 border-b border-gray-800">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Payment History</h3>
            </div>
            {data.payments && data.payments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Date</th>
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Product</th>
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Amount</th>
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Status</th>
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500 hidden lg:table-cell">Reference</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {data.payments.map((payment: any) => {
                      const payStatus = STATUS_CONFIG[payment.status] || STATUS_CONFIG.pending
                      const StatusIcon = payStatus.icon
                      return (
                        <tr key={payment.id} className="hover:bg-gray-800/20">
                          <td className="px-4 py-3 text-sm text-gray-300">{formatDateTime(payment.created_at)}</td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-white capitalize">{payment.product?.replace(/_/g, ' ')}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-white font-bold">{formatKobo(payment.amount)}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${payStatus.bg} ${payStatus.color} ${payStatus.border}`}>
                              <StatusIcon size={10} />
                              {payStatus.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 font-mono hidden lg:table-cell">{payment.paystack_reference || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <DollarSign size={48} className="text-gray-700 mx-auto mb-3" />
                <p className="text-gray-400 font-bold">No payments found</p>
                <p className="text-sm text-gray-600 mt-1">This user hasn't made any payments yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== REFERRALS TAB ===== */}
      {activeTab === 'referrals' && (
        <div className="space-y-6">
          {/* Referral Code */}
          <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4">Referral Code</h3>
            {profile?.referral_code ? (
              <div className="flex items-center gap-3">
                <span className="px-4 py-2 rounded-xl bg-gray-900 border border-gray-700 font-mono text-lg text-[#7cff01] tracking-widest">
                  {profile.referral_code}
                </span>
                <button
                  onClick={() => { navigator.clipboard.writeText(profile.referral_code); flash('success', 'Referral code copied') }}
                  className="px-3 py-2 rounded-lg bg-gray-800 text-gray-300 hover:text-white text-xs font-bold transition"
                >
                  Copy
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No referral code generated</p>
            )}
          </div>

          {/* Referred By */}
          <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4">Referred By</h3>
            {data.referral?.referredBy ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center border border-gray-700">
                  <User size={18} className="text-gray-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{data.referral.referredBy.full_name || 'Unknown'}</p>
                  <p className="text-xs text-gray-500">{data.referral.referredBy.email}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">This user was not referred by anyone</p>
            )}
          </div>

          {/* Downlines */}
          <div className="rounded-2xl border border-gray-800 bg-[#0d1117] overflow-hidden">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Referrals ({data.referral?.downlines?.length || 0})</h3>
              <div className="flex gap-3 text-xs text-gray-500">
                <span>Paid: <span className="text-yellow-400 font-bold">{data.stats?.paidDownlines || 0}</span></span>
              </div>
            </div>
            {data.referral?.downlines && data.referral.downlines.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">User</th>
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Role</th>
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Status</th>
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Subscription</th>
                      <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500 hidden md:table-cell">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {data.referral.downlines.map((d: any) => {
                      const dBadge = ROLE_BADGES[d.role] || ROLE_BADGES.player
                      return (
                        <tr key={d.id} className="hover:bg-gray-800/20">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center border border-gray-700">
                                <User size={14} className="text-gray-500" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-white">{d.full_name || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">{d.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${dBadge.color}`}>
                              {dBadge.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${d.is_verified ? 'text-green-400' : 'text-yellow-400'}`}>
                              {d.is_verified ? 'Verified' : 'Unverified'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {d.subscription?.isPro ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                                <Crown size={10} /> PRO
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500">Free</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400 hidden md:table-cell">{formatDate(d.created_at)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Users size={48} className="text-gray-700 mx-auto mb-3" />
                <p className="text-gray-400 font-bold">No referrals yet</p>
                <p className="text-sm text-gray-600 mt-1">This user hasn't referred anyone yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== DASHBOARD TAB ===== */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Player Dashboard View */}
          {data.player && (
            <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
              <div className="flex items-center gap-3 mb-4">
                <User size={20} className="text-[#7cff01]" />
                <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Player Record</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Name</p>
                  <p className="text-sm text-white">{data.player.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Position</p>
                  <p className="text-sm text-white">{data.player.main_position || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Club</p>
                  <p className="text-sm text-white">{data.player.club || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Age</p>
                  <p className="text-sm text-white">{data.player.age || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">State</p>
                  <p className="text-sm text-white">{data.player.state || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <div className="flex gap-2">
                    {data.player.is_verified && <span className="text-xs text-green-400">✓ Verified</span>}
                    {data.player.is_public && <span className="text-xs text-blue-400">◉ Public</span>}
                    {data.player.is_top_talent && <span className="text-xs text-yellow-400">★ Top Talent</span>}
                    {!data.player.is_verified && !data.player.is_public && !data.player.is_top_talent && <span className="text-xs text-gray-500">—</span>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Club Dashboard View */}
          {data.club && (
            <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield size={20} className="text-purple-400" />
                <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Club Record</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Club Name</p>
                  <p className="text-sm text-white">{data.club.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Location</p>
                  <p className="text-sm text-white">{data.club.location || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Verified</p>
                  <p className="text-sm text-white">{data.club.is_verified ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Visible</p>
                  <p className="text-sm text-white">{data.club.is_visible ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </div>
          )}

          {/* No dashboard data */}
          {!data.player && !data.club && (
            <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-12 text-center">
              <Activity size={48} className="text-gray-700 mx-auto mb-3" />
              <p className="text-gray-400 font-bold">No dashboard data available</p>
              <p className="text-sm text-gray-600 mt-1">
                {profile?.role === 'scout'
                  ? 'Scout dashboards show saved players and activity'
                  : 'This user has no player or club record linked'}
              </p>
            </div>
          )}

          {/* Quick Stats */}
          <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-4">Account Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl bg-gray-900 p-4 border border-gray-800">
                <p className="text-xs text-gray-500">Role</p>
                <p className="text-lg font-bold text-white capitalize">{profile?.role?.replace('_', ' ')}</p>
              </div>
              <div className="rounded-xl bg-gray-900 p-4 border border-gray-800">
                <p className="text-xs text-gray-500">Verified</p>
                <p className="text-lg font-bold text-white">{profile?.is_verified ? 'Yes' : 'No'}</p>
              </div>
              <div className="rounded-xl bg-gray-900 p-4 border border-gray-800">
                <p className="text-xs text-gray-500">Pro Status</p>
                <p className={`text-lg font-bold ${isPro ? 'text-yellow-400' : 'text-gray-400'}`}>{isPro ? 'Active' : 'Free'}</p>
              </div>
              <div className="rounded-xl bg-gray-900 p-4 border border-gray-800">
                <p className="text-xs text-gray-500">Referrals</p>
                <p className="text-lg font-bold text-white">{data.stats?.downlineCount || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== DELETE MODAL ===== */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !deleteLoading && setShowDeleteModal(false)}
        >
          <div
            className="w-full max-w-md bg-[#161b22] rounded-2xl border border-gray-800 p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${deleteMode === 'delete' ? 'bg-red-500/10' : 'bg-yellow-500/10'}`}>
                <Trash2 size={20} className={deleteMode === 'delete' ? 'text-red-400' : 'text-yellow-400'} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">{deleteMode === 'delete' ? 'Delete Account' : 'Suspend Account'}</h3>
                <p className="text-xs text-gray-500">{profile?.email}</p>
              </div>
            </div>

            {/* Mode toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setDeleteMode('archive')}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition ${deleteMode === 'archive' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' : 'bg-gray-800 text-gray-500 border border-gray-700'}`}
              >
                Suspend
              </button>
              <button
                onClick={() => setDeleteMode('delete')}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition ${deleteMode === 'delete' ? 'bg-red-500/10 text-red-400 border border-red-500/30' : 'bg-gray-800 text-gray-500 border border-gray-700'}`}
              >
                Permanent Delete
              </button>
            </div>

            {deleteMode === 'delete' ? (
              <ul className="space-y-1.5 text-xs text-gray-400 mb-4">
                <li>• User account and auth credentials</li>
                <li>• Profile and subscription data</li>
                <li>• All linked player records</li>
                <li>• All conversations and messages</li>
                <li className="text-red-400 font-bold">• This action cannot be undone</li>
              </ul>
            ) : (
              <ul className="space-y-1.5 text-xs text-gray-400 mb-4">
                <li>• User will be hidden from explore</li>
                <li>• Linked players will be hidden</li>
                <li>• Data is preserved and can be restored</li>
              </ul>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2.5 rounded-xl bg-gray-800 text-gray-300 text-sm font-bold hover:text-white transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition disabled:opacity-50 ${
                  deleteMode === 'delete' ? 'bg-red-500 text-white hover:bg-red-400' : 'bg-yellow-500 text-black hover:bg-yellow-400'
                }`}
              >
                {deleteLoading ? <Loader2 size={16} className="animate-spin mx-auto" /> : deleteMode === 'delete' ? 'Delete Permanently' : 'Suspend Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
